import React, { useState, useEffect } from 'react';
import {
  MessageSquare, Search,
  User, Calendar, Send, Trash2,
  RefreshCw, X, Plus
} from 'lucide-react';
import { supportAPI } from '../lib/api';
import toast from 'react-hot-toast';

const Support = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [newResponse, setNewResponse] = useState('');
  const [sendingResponse, setSendingResponse] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    subject: '',
    message: '',
    category: 'general',
    priority: 'medium'
  });

  useEffect(() => {
    fetchTickets();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, priorityFilter, searchQuery]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (priorityFilter !== 'all') params.priority = priorityFilter;
      if (searchQuery) params.search = searchQuery;
      
      const data = await supportAPI.getAll(params);
      setTickets(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Ticket getirme hatası:', error);
      toast.error('Ticketler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await supportAPI.getStats();
      setStats(data);
    } catch (error) {
      console.error('İstatistik hatası:', error);
    }
  };

  const handleViewTicket = async (ticket) => {
    try {
      const fullTicket = await supportAPI.getById(ticket.id);
      setSelectedTicket(fullTicket);
      setShowDetail(true);
    } catch (error) {
      console.error('Ticket detay hatası:', error);
      toast.error('Ticket detayı yüklenemedi');
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await supportAPI.updateStatus(ticketId, newStatus);
      toast.success('Durum güncellendi');
      fetchTickets();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, status: newStatus });
      }
    } catch (error) {
      console.error('Durum güncelleme hatası:', error);
      toast.error('Durum güncellenemedi');
    }
  };

  const handlePriorityChange = async (ticketId, newPriority) => {
    try {
      await supportAPI.updatePriority(ticketId, newPriority);
      toast.success('Öncelik güncellendi');
      fetchTickets();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, priority: newPriority });
      }
    } catch (error) {
      console.error('Öncelik güncelleme hatası:', error);
      toast.error('Öncelik güncellenemedi');
    }
  };

  const handleSendResponse = async () => {
    if (!newResponse.trim()) return;
    
    try {
      setSendingResponse(true);
      await supportAPI.addResponse(selectedTicket.id, newResponse, true);
      setNewResponse('');
      const fullTicket = await supportAPI.getById(selectedTicket.id);
      setSelectedTicket(fullTicket);
      toast.success('Yanıt gönderildi');
    } catch (error) {
      console.error('Yanıt gönderme hatası:', error);
      toast.error('Yanıt gönderilemedi');
    } finally {
      setSendingResponse(false);
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    if (!window.confirm('Bu ticket silinecek. Emin misiniz?')) return;
    
    try {
      await supportAPI.delete(ticketId);
      toast.success('Ticket silindi');
      setShowDetail(false);
      setSelectedTicket(null);
      fetchTickets();
      fetchStats();
    } catch (error) {
      console.error('Ticket silme hatası:', error);
      toast.error('Ticket silinemedi');
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    try {
      await supportAPI.create(createFormData);
      toast.success('Ticket oluşturuldu');
      setShowCreateModal(false);
      setCreateFormData({ subject: '', message: '', category: 'general', priority: 'medium' });
      fetchTickets();
      fetchStats();
    } catch (error) {
      console.error('Ticket oluşturma hatası:', error);
      toast.error('Ticket oluşturulamadı');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
      in_progress: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
      resolved: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
      closed: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
    };
    return colors[status] || colors.open;
  };

  const getStatusText = (status) => {
    const texts = {
      open: 'Açık',
      in_progress: 'İşlemde',
      resolved: 'Çözüldü',
      closed: 'Kapalı'
    };
    return texts[status] || status;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
      medium: 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400',
      high: 'bg-orange-100 text-orange-600 dark:bg-orange-900 dark:text-orange-400',
      urgent: 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-400'
    };
    return colors[priority] || colors.medium;
  };

  const getPriorityText = (priority) => {
    const texts = {
      low: 'Düşük',
      medium: 'Orta',
      high: 'Yüksek',
      urgent: 'Acil'
    };
    return texts[priority] || priority;
  };

  const getCategoryText = (category) => {
    const texts = {
      general: 'Genel',
      technical: 'Teknik',
      billing: 'Fatura',
      sales: 'Satış'
    };
    return texts[category] || category;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredTickets = tickets.filter(ticket => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return ticket.subject?.toLowerCase().includes(query) ||
             ticket.message?.toLowerCase().includes(query) ||
             ticket.customer?.name?.toLowerCase().includes(query) ||
             ticket.customer?.email?.toLowerCase().includes(query);
    }
    return true;
  });

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <MessageSquare className="h-7 w-7" />
          Destek Talepleri
        </h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2"
        >
          <Plus size={18} />
          Yeni Ticket
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-500 dark:text-gray-400">Toplam</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total || 0}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-blue-600">Açık</div>
            <div className="text-2xl font-bold text-blue-600">{stats.open || 0}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-yellow-600">İşlemde</div>
            <div className="text-2xl font-bold text-yellow-600">{stats.inProgress || 0}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-green-600">Çözüldü</div>
            <div className="text-2xl font-bold text-green-600">{stats.resolved || 0}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-500 dark:text-gray-400">Kapalı</div>
            <div className="text-2xl font-bold text-gray-500">{stats.closed || 0}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-orange-600">Yüksek Öncelik</div>
            <div className="text-2xl font-bold text-orange-600">{stats.highPriority || 0}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchTickets()}
                className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg"
          >
            <option value="all">Tüm Durumlar</option>
            <option value="open">Açık</option>
            <option value="in_progress">İşlemde</option>
            <option value="resolved">Çözüldü</option>
            <option value="closed">Kapalı</option>
          </select>
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg"
          >
            <option value="all">Tüm Öncelikler</option>
            <option value="low">Düşük</option>
            <option value="medium">Orta</option>
            <option value="high">Yüksek</option>
            <option value="urgent">Acil</option>
          </select>
          <button
            onClick={() => { fetchTickets(); fetchStats(); }}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Yenile
          </button>
        </div>
      </div>

      {/* Tickets List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Yükleniyor...</div>
        ) : filteredTickets.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Ticket bulunamadı</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Konu</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Müşteri</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Kategori</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Öncelik</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Durum</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tarih</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Yanıtlar</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredTickets.map((ticket) => (
                <tr key={ticket.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleViewTicket(ticket)}
                      className="text-left hover:text-blue-600"
                    >
                      <div className="font-medium text-gray-900 dark:text-white">{ticket.subject}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                        {ticket.message?.substring(0, 50)}...
                      </div>
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900 dark:text-white">{ticket.customer?.name || '-'}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{ticket.customer?.email || '-'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {getCategoryText(ticket.category)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={ticket.priority}
                      onChange={(e) => handlePriorityChange(ticket.id, e.target.value)}
                      className={`text-xs px-2 py-1 rounded-full ${getPriorityColor(ticket.priority)} border-0 cursor-pointer`}
                    >
                      <option value="low">Düşük</option>
                      <option value="medium">Orta</option>
                      <option value="high">Yüksek</option>
                      <option value="urgent">Acil</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={ticket.status}
                      onChange={(e) => handleStatusChange(ticket.id, e.target.value)}
                      className={`text-xs px-2 py-1 rounded-full ${getStatusColor(ticket.status)} border-0 cursor-pointer`}
                    >
                      <option value="open">Açık</option>
                      <option value="in_progress">İşlemde</option>
                      <option value="resolved">Çözüldü</option>
                      <option value="closed">Kapalı</option>
                    </select>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(ticket.created_at)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center justify-center w-6 h-6 bg-gray-100 dark:bg-gray-700 rounded-full text-xs text-gray-600 dark:text-gray-400">
                      {ticket.responses?.length || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleDeleteTicket(ticket.id)}
                      className="p-1 text-red-600 hover:bg-red-100 dark:hover:bg-red-900 rounded"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Ticket Detail Modal */}
      {showDetail && selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">{selectedTicket.subject}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(selectedTicket.status)}`}>
                    {getStatusText(selectedTicket.status)}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${getPriorityColor(selectedTicket.priority)}`}>
                    {getPriorityText(selectedTicket.priority)}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {getCategoryText(selectedTicket.category)}
                  </span>
                </div>
              </div>
              <button
                onClick={() => { setShowDetail(false); setSelectedTicket(null); }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            {/* Customer Info */}
            <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-2 text-sm">
                <User size={14} className="text-gray-400" />
                <span className="text-gray-700 dark:text-gray-300">
                  {selectedTicket.customer?.name || 'Bilinmiyor'}
                </span>
                {selectedTicket.customer?.email && (
                  <span className="text-gray-500 dark:text-gray-400">
                    ({selectedTicket.customer.email})
                  </span>
                )}
                <span className="ml-auto text-xs text-gray-400">
                  <Calendar size={12} className="inline mr-1" />
                  {formatDate(selectedTicket.created_at)}
                </span>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Original Message */}
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <div className="text-xs text-blue-600 dark:text-blue-400 mb-1 flex items-center gap-1">
                  <User size={12} />
                  {selectedTicket.customer?.name || 'Müşteri'}
                </div>
                <p className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{selectedTicket.message}</p>
              </div>

              {/* Responses */}
              {selectedTicket.responses?.map((response) => (
                <div
                  key={response.id}
                  className={`p-4 rounded-lg ${
                    response.is_admin
                      ? 'bg-green-50 dark:bg-green-900/20 ml-8'
                      : 'bg-blue-50 dark:bg-blue-900/20 mr-8'
                  }`}
                >
                  <div className={`text-xs mb-1 flex items-center gap-1 ${
                    response.is_admin ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'
                  }`}>
                    <User size={12} />
                    {response.is_admin ? 'Destek Ekibi' : selectedTicket.customer?.name || 'Müşteri'}
                    <span className="ml-auto text-gray-400">{formatDate(response.created_at)}</span>
                  </div>
                  <p className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{response.message}</p>
                </div>
              ))}
            </div>

            {/* Response Form */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex gap-2">
                <textarea
                  value={newResponse}
                  onChange={(e) => setNewResponse(e.target.value)}
                  placeholder="Yanıt yazın..."
                  rows={3}
                  className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg resize-none"
                />
                <button
                  onClick={handleSendResponse}
                  disabled={!newResponse.trim() || sendingResponse}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg flex items-center gap-2 h-fit"
                >
                  <Send size={18} />
                  Gönder
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Ticket Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Yeni Ticket Oluştur</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateTicket} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Konu</label>
                <input
                  type="text"
                  value={createFormData.subject}
                  onChange={(e) => setCreateFormData({ ...createFormData, subject: e.target.value })}
                  required
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Kategori</label>
                  <select
                    value={createFormData.category}
                    onChange={(e) => setCreateFormData({ ...createFormData, category: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg"
                  >
                    <option value="general">Genel</option>
                    <option value="technical">Teknik</option>
                    <option value="billing">Fatura</option>
                    <option value="sales">Satış</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Öncelik</label>
                  <select
                    value={createFormData.priority}
                    onChange={(e) => setCreateFormData({ ...createFormData, priority: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg"
                  >
                    <option value="low">Düşük</option>
                    <option value="medium">Orta</option>
                    <option value="high">Yüksek</option>
                    <option value="urgent">Acil</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mesaj</label>
                <textarea
                  value={createFormData.message}
                  onChange={(e) => setCreateFormData({ ...createFormData, message: e.target.value })}
                  required
                  rows={5}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg resize-none"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                >
                  Oluştur
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Support;
